"use client"

import { useState, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'
import { Check, Monitor } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { DensityPicker } from '@/components/density-picker'
import { useStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { applyTheme, getCurrentTheme, oppositeTheme, themeFamily } from '@/lib/theme'
import { getTouchKeyboard, setTouchKeyboard } from '@/lib/touch-keyboard'
import { finishScreenFit, isTouchScreen, readScreenFit, shouldOfferScreenFit, type ScreenFitState } from '@/lib/screen-fit'

const WIDE = '(min-width: 768px)'

/** Watched live (Codex): a tablet turned from portrait, a window widened later. */
function useWide(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia(WIDE)
      mq.addEventListener('change', cb)
      return () => mq.removeEventListener('change', cb)
    },
    () => window.matchMedia(WIDE).matches,
    () => false,
  )
}

/**
 * „მოარგე ეკრანი" — the owner's idea (2026-10-09): on a new computer or tablet,
 * before anything else, the size, light / dark and — on a touch screen — the
 * on-screen keyboard. Each choice applies at once (the whole app, this window
 * included, changes under the pointer). Only „დაწყება" ends it — a stray touch
 * must not lose it for good (Codex). Spec §1–§2; rules in lib/screen-fit.ts.
 */
export function ScreenFitDialog() {
  const t = useT()
  const { pathname } = useLocation()
  const wide = useWide()
  const locked = useStore((s) => s.isLocked)
  const kiosk = useStore((s) => s.kioskMode)
  const support = useStore((s) => s.supportSession !== null)
  const [state, setState] = useState<ScreenFitState>(() => readScreenFit())
  const [family, setFamily] = useState<'light' | 'dark'>(() => themeFamily(getCurrentTheme()))
  const [keyboard, setKeyboard] = useState<boolean>(() => getTouchKeyboard())
  const [touch] = useState<boolean>(() => isTouchScreen({
    coarse: window.matchMedia('(pointer: coarse)').matches,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  }))

  if (!shouldOfferScreenFit({ state, wide, path: pathname, support, locked, kiosk })) return null

  const pickFamily = (f: 'light' | 'dark') => {
    const current = getCurrentTheme()
    if (themeFamily(current) !== f) applyTheme(oppositeTheme(current))
    setFamily(f)
  }
  const pickKeyboard = (on: boolean) => { setTouchKeyboard(on); setKeyboard(on) }
  const start = () => { finishScreenFit(); setState('done') }

  return (
    // Controlled and never closed by Base UI: Escape and an outside press ask
    // to close, and nothing changes — only „დაწყება" ends it.
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-black/60 supports-backdrop-filter:backdrop-blur-none"
        className="sm:max-w-xl gap-5 p-6"
      >
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Monitor className="size-5" />
          </span>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-bold">{t('screenFit.title')}</DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground">{t('screenFit.lede')}</DialogDescription>
          </div>
        </div>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t('screenFit.size')}</h3>
          <DensityPicker />
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t('screenFit.colour')}</h3>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('screenFit.colour')}>
            <Choice on={family === 'light'} onPick={() => pickFamily('light')} label={t('screenFit.light')} hint={t('screenFit.light.hint')} />
            <Choice on={family === 'dark'} onPick={() => pickFamily('dark')} label={t('screenFit.dark')} hint={t('screenFit.dark.hint')} />
          </div>
        </section>

        {touch && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">{t('screenFit.touch')}</h3>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('screenFit.touch')}>
              <Choice on={!keyboard} onPick={() => pickKeyboard(false)} label={t('screenFit.touch.no')} hint={t('screenFit.touch.no.hint')} />
              <Choice on={keyboard} onPick={() => pickKeyboard(true)} label={t('screenFit.touch.yes')} hint={t('screenFit.touch.yes.hint')} />
            </div>
          </section>
        )}

        <div className="flex items-center justify-between gap-4 pt-1">
          <p className="text-[12px] text-muted-foreground">{t('screenFit.later')}</p>
          <Button onClick={start} className="h-11 shrink-0 px-6 text-sm font-semibold">{t('screenFit.start')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** One radio card — the same look as DensityPicker's, so the window reads as one. */
function Choice({ on, onPick, label, hint }: { on: boolean; onPick: () => void; label: string; hint: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onPick}
      className={cn(
        'relative text-left rounded-xl border px-3.5 py-3 transition-colors',
        on ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/60',
      )}
    >
      <span className={cn('block text-sm font-bold', on ? 'text-primary' : 'text-foreground')}>{label}</span>
      <span className="block text-[12px] text-muted-foreground mt-0.5 leading-snug">{hint}</span>
      {on && <Check className="absolute top-3 right-3 size-4 text-primary" />}
    </button>
  )
}
